import React, { useState, useEffect, useRef } from 'react';
import { Arrow, Files, General } from 'icons/untitledui';

// styles
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import * as Text from 'theme/components/Text.style';

interface SelectMenuItemWrapperProps {
  option: { value: string; label: string };
  selected: boolean;
  onClick: () => void;
}

const SelectMenuItemWrapper: React.FC<SelectMenuItemWrapperProps> = ({
  option,
  selected,
  onClick,
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isWrapped, setIsWrapped] = useState(false);

  useEffect(() => {
    const checkWrapping = () => {
      if (textRef.current) {
        // Check if text wraps by comparing scrollHeight to line-height
        // If scrollHeight is significantly greater than one line, it's wrapped
        const lineHeight = parseFloat(
          window.getComputedStyle(textRef.current).lineHeight
        );
        const isTextWrapped = textRef.current.scrollHeight > lineHeight * 1.5;
        setIsWrapped(isTextWrapped);
      }
    };

    let resizeObserver: ResizeObserver | null = null;

    // Small delay to ensure ref is attached
    const timeoutId = setTimeout(() => {
      if (textRef.current) {
        // Use ResizeObserver for more reliable detection
        resizeObserver = new ResizeObserver(checkWrapping);
        resizeObserver.observe(textRef.current);

        // Initial check
        checkWrapping();
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [option.label]);

  return (
    <StyledPromptControls.SelectMenuItem onClick={onClick} selected={selected}>
      <StyledPromptControls.SelectMenuItemContainer isWrapped={isWrapped}>
        <StyledPromptControls.SelectMenuItemIcon isWrapped={isWrapped}>
          <Files.File02 size={18} strokeWidth={2} />
        </StyledPromptControls.SelectMenuItemIcon>
        <StyledPromptControls.SelectMenuItemText ref={textRef}>
          {option.label}
        </StyledPromptControls.SelectMenuItemText>
        {selected && (
          <StyledPromptControls.SelectMenuItemCheck>
            <General.Check size={16} />
          </StyledPromptControls.SelectMenuItemCheck>
        )}
      </StyledPromptControls.SelectMenuItemContainer>
    </StyledPromptControls.SelectMenuItem>
  );
};

interface ClickableInputProps {
  className?: string;
  inputAction?: (value?: string) => void;
  inputText?: string;
  inputType?: string;
  selectOptions?: { value: string; label: string }[];
  selectedValue?: string;
  onSelectChange?: (value: string) => void;
  'data-tour-target'?: string;
}

export default function ClickableInput({
  className,
  inputAction,
  inputText,
  inputType,
  selectOptions = [],
  selectedValue: controlledValue,
  onSelectChange,
  'data-tour-target': dataTourTarget,
}: ClickableInputProps): JSX.Element {
  const [internalValue, setInternalValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isControlled = controlledValue !== undefined;
  const selectedValue = isControlled ? controlledValue : internalValue;

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (inputAction) {
      inputAction();
    }
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMenuItemClick = (value: string) => {
    if (!isControlled) {
      setInternalValue(value);
    }
    if (onSelectChange) {
      onSelectChange(value);
    }
    handleClose();
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  if (inputType === 'select')
    return (
      <div style={{ position: 'relative' }}>
        <StyledPromptControls.PromptControlSelect
          ref={buttonRef}
          className={className || ''}
          onClick={handleButtonClick}
          data-tour-target={dataTourTarget}
          endIcon={
            <Arrow.ChevronDown
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          }
        >
          <Text.P2Semibold>{inputText || 'Select...'}</Text.P2Semibold>
        </StyledPromptControls.PromptControlSelect>
        <StyledPromptControls.SelectMenuContainer ref={menuRef} open={open}>
          <StyledPromptControls.SelectMenuList>
            {selectOptions.map((option) => (
              <SelectMenuItemWrapper
                key={option.value}
                option={option}
                selected={selectedValue === option.value}
                onClick={() => handleMenuItemClick(option.value)}
              />
            ))}
          </StyledPromptControls.SelectMenuList>
        </StyledPromptControls.SelectMenuContainer>
      </div>
    );

  if (inputType === 'dark')
    return (
      <StyledPromptControls.PromptControlDark
        className={className || ''}
        onClick={inputAction}
        data-tour-target={dataTourTarget}
      >
        <Text.P2Semibold>{inputText}</Text.P2Semibold>
      </StyledPromptControls.PromptControlDark>
    );

  if (inputType === 'primary')
    return (
      <StyledPromptControls.PromptControlPrimary
        className={className || ''}
        onClick={inputAction}
        data-tour-target={dataTourTarget}
      >
        <Text.P2Semibold>{inputText}</Text.P2Semibold>
      </StyledPromptControls.PromptControlPrimary>
    );

  // default: if (inputType === 'light')
  return (
    <StyledPromptControls.PromptControl
      className={className || ''}
      onClick={inputAction}
      data-tour-target={dataTourTarget}
    >
      <Text.P2Semibold>{inputText}</Text.P2Semibold>
    </StyledPromptControls.PromptControl>
  );
}
